// Column-based layout utilities for stacking components
// ========================================================
// COLUMN GROUPING + AUTO REFLOW SYSTEM
// 
// Rules:
// 1. All components in a column share the same width and depth
// 2. Changing width/depth of ANY component updates the entire column
// 3. Changing column width causes horizontal reflow (no gaps)
// 4. Changing height causes vertical reflow within column (no gaps)
// 5. Columns are contiguous from left to right (no floating columns)
// ========================================================

/**
 * Derive columns from items on a wall
 * Items with the same X position and width are grouped into the same column
 *
 * Options:
 *   - `skipContainerChildren` (default true): skip items with `parentContainerId`
 *     so MultiColumnContainer children don't form their own wall-level columns.
 *     The 3D builder explicitly passes `false` to render the children as
 *     side-by-side cabinets (see `features/view-3d/utils/buildRoom.js`).
 *   - `skipContainers` (default false): skip the container item itself (it
 *     has no rendered geometry; children carry the visuals in 3D).
 */
export const deriveColumnsFromItems = (items, wallId, options = {}) => {
  const { skipContainerChildren = true, skipContainers = false } = options;
  const wallItems = items.filter((item) => {
    if (item.wallId !== wallId) return false;
    if (skipContainerChildren && item.parentContainerId) return false;
    if (skipContainers && (item.category === 'multi-column-container' || item.templateId === 'multi-column-container')) return false;
    return true;
  });
  
  // Group items by their X position (with small tolerance for rounding)
  const columnMap = new Map();
  
  wallItems.forEach(item => {
    // Round X to avoid floating point issues
    const columnX = Math.round(item.x * 100) / 100;
    const columnKey = `${columnX}-${item.width}`;
    
    if (!columnMap.has(columnKey)) {
      columnMap.set(columnKey, {
        x: item.x,
        width: item.width,
        depth: item.depth, // Track column depth
        items: [],
      });
    }
    const col = columnMap.get(columnKey);
    col.items.push(item);
    // Use max depth from all items in column
    if (item.depth > col.depth) {
      col.depth = item.depth;
    }
  });
  
  // Convert map to array and sort by X position
  const columns = Array.from(columnMap.values())
    .sort((a, b) => a.x - b.x)
    .map((col, index) => ({
      ...col,
      id: `column-${index}`,
      order: index,
      // Sort items within column by Y position (bottom to top)
      items: col.items.sort((a, b) => a.y - b.y),
    }));
  
  return columns;
};

/**
 * Get all items in the same column as the given item
 * Returns array of item IDs
 */
export const getColumnItemIds = (items, wallId, itemId) => {
  const column = getColumnForItem(items, wallId, itemId);
  if (!column) return [];
  return column.items.map(i => i.id);
};

/**
 * Calculate horizontal reflow after column width change
 * Returns array of { id, x } updates for all affected items
 * 
 * @param {Array} items - All project items
 * @param {string} wallId - Wall ID
 * @param {string} changedItemId - ID of item whose width changed
 * @param {number} newWidth - New width value
 * @param {number} startOffset - Buffer offset from wall start (default 0)
 * @returns {{ updates: Array, error: string|null }}
 */
export const calculateHorizontalReflow = (items, wallId, changedItemId, newWidth, startOffset = 0) => {
  const columns = deriveColumnsFromItems(items, wallId);
  
  // Find which column contains the changed item
  let changedColumnIndex = -1;
  for (let i = 0; i < columns.length; i++) {
    if (columns[i].items.some(item => item.id === changedItemId)) {
      changedColumnIndex = i;
      break;
    }
  }
  
  if (changedColumnIndex === -1) {
    return { updates: [], error: 'Item not found in any column' };
  }
  
  const changedColumn = columns[changedColumnIndex];
  const oldWidth = changedColumn.width;
  const widthDelta = newWidth - oldWidth;
  
  // If width didn't change, no reflow needed
  if (Math.abs(widthDelta) < 0.01) {
    return { updates: [], error: null };
  }
  
  const updates = [];
  
  // Update all items in the changed column with new width
  changedColumn.items.forEach(item => {
    updates.push({ id: item.id, width: newWidth });
  });
  
  // Shift all columns to the RIGHT of the changed column
  for (let i = changedColumnIndex + 1; i < columns.length; i++) {
    const col = columns[i];
    const newX = col.x + widthDelta;
    
    col.items.forEach(item => {
      updates.push({ id: item.id, x: newX });
    });
  }
  
  return { updates, error: null };
};

/**
 * Calculate depth sync for all items in a column
 * Returns array of { id, depth } updates
 */
export const calculateDepthSync = (items, wallId, changedItemId, newDepth) => {
  const column = getColumnForItem(items, wallId, changedItemId);
  if (!column) {
    return { updates: [], error: 'Item not found in any column' };
  }
  
  const updates = [];
  column.items.forEach(item => {
    if (item.depth !== newDepth) {
      updates.push({ id: item.id, depth: newDepth });
    }
  });
  
  return { updates, error: null };
};

/**
 * Validate column width change against wall constraints
 * 
 * @param {Array} items - All project items
 * @param {string} wallId - Wall ID
 * @param {string} changedItemId - ID of item whose width is changing
 * @param {number} newWidth - Proposed new width
 * @param {number} wallLength - Total wall length
 * @param {Object} usableInfo - { startOffset, endOffset, usableWidth } from cornerBuffer
 * @returns {{ valid: boolean, error: string|null }}
 */
export const validateColumnWidthChange = (items, wallId, changedItemId, newWidth, wallLength, usableInfo) => {
  const { startOffset = 0, endOffset = 0, usableWidth } = usableInfo || {};
  const columns = deriveColumnsFromItems(items, wallId);
  
  // Find the changed column
  let changedColumnIndex = -1;
  for (let i = 0; i < columns.length; i++) {
    if (columns[i].items.some(item => item.id === changedItemId)) {
      changedColumnIndex = i;
      break;
    }
  }
  
  if (changedColumnIndex === -1) {
    return { valid: false, error: 'Item not found' };
  }
  
  const changedColumn = columns[changedColumnIndex];
  const widthDelta = newWidth - changedColumn.width;
  
  // Calculate new total width
  const currentTotalWidth = columns.reduce((sum, col) => sum + col.width, 0);
  const newTotalWidth = currentTotalWidth + widthDelta;
  
  // Check against usable width (accounting for buffers)
  const maxUsable = usableWidth !== undefined ? usableWidth : wallLength;
  
  if (newTotalWidth > maxUsable) {
    return {
      valid: false,
      error: `Total width (${newTotalWidth.toFixed(1)}") exceeds usable wall width (${maxUsable.toFixed(1)}")`,
    };
  }
  
  // Check if rightmost column would extend into end buffer
  if (endOffset > 0) {
    const rightmostColumnEnd = startOffset + newTotalWidth;
    if (rightmostColumnEnd > wallLength - endOffset) {
      return {
        valid: false,
        error: `Layout would extend into corner buffer zone`,
      };
    }
  }
  
  return { valid: true, error: null };
};

/**
 * Recalculate all column X positions to ensure no gaps
 * Call this after any column is deleted or width changes
 * 
 * @param {Array} items - All project items  
 * @param {string} wallId - Wall ID
 * @param {number} startOffset - Buffer offset from wall start (default 0)
 * @returns {Array} Array of { id, x } updates
 */
export const recalculateColumnPositions = (items, wallId, startOffset = 0) => {
  const columns = deriveColumnsFromItems(items, wallId);
  const updates = [];
  
  let currentX = startOffset;
  
  columns.forEach(column => {
    // If column X doesn't match expected position, update all items
    if (Math.abs(column.x - currentX) > 0.01) {
      column.items.forEach(item => {
        updates.push({ id: item.id, x: currentX });
      });
    }
    currentX += column.width;
  });
  
  return updates;
};

/**
 * Calculate vertical reflow within a column after height change
 * Ensures no gaps between stacked items
 * 
 * @param {Object} column - Column object with items
 * @param {string} changedItemId - ID of item whose height changed
 * @param {number} newHeight - New height value
 * @returns {Array} Array of { id, y, height } updates
 */
export const calculateVerticalReflow = (column, changedItemId, newHeight) => {
  if (!column || column.items.length === 0) {
    return [];
  }
  
  // Sort items by Y (bottom to top)
  const sortedItems = [...column.items].sort((a, b) => a.y - b.y);
  
  // Apply the height change to the target item
  const updatedItems = sortedItems.map(item => ({
    ...item,
    height: item.id === changedItemId ? newHeight : item.height,
  }));
  
  // Recalculate Y positions from bottom up (no gaps)
  const updates = [];
  let currentY = 0;
  
  updatedItems.forEach(item => {
    const update = { id: item.id };
    
    // Always include height update for the changed item
    if (item.id === changedItemId) {
      update.height = newHeight;
    }
    
    // Update Y if it changed
    if (Math.abs(item.y - currentY) > 0.01 || item.id === changedItemId) {
      update.y = currentY;
    }
    
    if (Object.keys(update).length > 1) { // Has more than just id
      updates.push(update);
    }
    
    currentY += item.id === changedItemId ? newHeight : item.height;
  });
  
  return updates;
};

/**
 * Calculate the total used width on a wall
 */
export const calculateUsedWidth = (items, wallId) => {
  const columns = deriveColumnsFromItems(items, wallId);
  return columns.reduce((total, col) => total + col.width, 0);
};

/**
 * Check if there's enough wall width for a new column
 */
export const canAddNewColumn = (items, wallId, wallWidth, newColumnWidth) => {
  const usedWidth = calculateUsedWidth(items, wallId);
  return usedWidth + newColumnWidth <= wallWidth;
};

/**
 * Find the next available X position for a new column
 */
export const findNextColumnX = (items, wallId) => {
  const columns = deriveColumnsFromItems(items, wallId);
  if (columns.length === 0) return 0;
  
  // Place new column right after the last column
  const lastColumn = columns[columns.length - 1];
  return lastColumn.x + lastColumn.width;
};

/**
 * Get column info for a specific item
 */
export const getColumnForItem = (items, wallId, itemId) => {
  const columns = deriveColumnsFromItems(items, wallId);
  for (const column of columns) {
    const item = column.items.find(i => i.id === itemId);
    if (item) {
      return column;
    }
  }
  return null;
};

/**
 * Calculate stack height in a column
 */
export const calculateStackHeight = (column) => {
  if (!column || column.items.length === 0) return 0;
  
  // Find the topmost point (highest Y + height)
  return Math.max(...column.items.map(item => item.y + item.height));
};

/**
 * Calculate free height above a column's stack
 */
export const calculateFreeHeight = (column, wallHeight) => {
  const stackHeight = calculateStackHeight(column);
  return Math.max(0, wallHeight - stackHeight);
};

/**
 * Get the Y position for stacking a new item on top
 */
export const getStackTopY = (column) => {
  if (!column || column.items.length === 0) return 0;
  return calculateStackHeight(column);
};

/**
 * Check if a component can be stacked in a column
 */
export const canStackInColumn = (column, componentHeight, wallHeight, minHeight = 0) => {
  const freeHeight = calculateFreeHeight(column, wallHeight);
  
  if (componentHeight > freeHeight) {
    return { canStack: false, reason: 'Not enough height available.' };
  }
  
  if (minHeight > 0 && freeHeight < minHeight) {
    return { canStack: false, reason: `Minimum height requirement not met (need ${minHeight}").` };
  }
  
  return { canStack: true, reason: null };
};

/**
 * Validate width match for stacking
 */
export const validateWidthMatch = (column, componentWidth) => {
  const tolerance = 0.01; // Small tolerance for floating point
  if (Math.abs(column.width - componentWidth) > tolerance) {
    return {
      valid: false,
      error: `Width must match column W = ${column.width.toFixed(1)}"`
    };
  }
  return { valid: true, error: null };
};

/**
 * Reorder columns horizontally (swap adjacent columns)
 * Returns new X positions for all items
 */
export const reorderColumns = (columns, fromIndex, toIndex) => {
  if (fromIndex === toIndex) return [];
  
  const reordered = [...columns];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, moved);
  
  // Recalculate X positions
  const updates = [];
  let currentX = 0;
  
  reordered.forEach(column => {
    column.items.forEach(item => {
      if (item.x !== currentX) {
        updates.push({ id: item.id, x: currentX });
      }
    });
    currentX += column.width;
  });
  
  return updates;
};

/**
 * Swap vertical order of two adjacent items within the same column
 * Returns { success, updates, error }
 */
export const swapVerticalOrder = (column, itemId, direction, wallHeight) => {
  const sortedItems = [...column.items].sort((a, b) => a.y - b.y);
  const itemIndex = sortedItems.findIndex(i => i.id === itemId);
  
  if (itemIndex === -1) {
    return { success: false, updates: [], error: 'Item not found in column' };
  }
  
  // Determine target index based on direction
  // 'up' means swap with item above (higher Y), 'down' means swap with item below (lower Y)
  const targetIndex = direction === 'up' ? itemIndex + 1 : itemIndex - 1;
  
  if (targetIndex < 0 || targetIndex >= sortedItems.length) {
    return { success: false, updates: [], error: 'No item to swap with' };
  }
  
  // Create a copy and swap
  const swappedItems = [...sortedItems];
  [swappedItems[itemIndex], swappedItems[targetIndex]] = [swappedItems[targetIndex], swappedItems[itemIndex]];
  
  // Check if total height would exceed wall height
  const totalHeight = swappedItems.reduce((sum, item) => sum + item.height, 0);
  if (totalHeight > wallHeight) {
    return { success: false, updates: [], error: 'Swap would exceed wall height' };
  }
  
  // Recalculate Y positions from bottom up
  const updates = [];
  let currentY = 0;
  
  swappedItems.forEach(item => {
    updates.push({ id: item.id, y: currentY });
    currentY += item.height;
  });
  
  return { success: true, updates, error: null };
};

/**
 * Get vertical neighbors for an item in a column
 * Returns { itemAbove, itemBelow }
 */
export const getVerticalNeighbors = (column, itemId) => {
  const sortedItems = [...column.items].sort((a, b) => a.y - b.y);
  const itemIndex = sortedItems.findIndex(i => i.id === itemId);
  
  if (itemIndex === -1) {
    return { itemAbove: null, itemBelow: null };
  }
  
  return {
    itemAbove: itemIndex < sortedItems.length - 1 ? sortedItems[itemIndex + 1] : null,
    itemBelow: itemIndex > 0 ? sortedItems[itemIndex - 1] : null,
  };
};

/**
 * Get columns with free space info for rendering "+" buttons
 */
export const getColumnsWithFreeSpace = (items, wallId, wallHeight) => {
  const columns = deriveColumnsFromItems(items, wallId);
  
  return columns
    .filter(col => col.items.length > 0) // Only non-empty columns
    .map(col => ({
      ...col,
      stackHeight: calculateStackHeight(col),
      freeHeight: calculateFreeHeight(col, wallHeight),
      stackTopY: getStackTopY(col),
    }))
    .filter(col => col.freeHeight > 0); // Only columns with free space
};

/**
 * Constants for column rules
 */
export const COLUMN_CONSTANTS = {
  HANGING_MIN_HEIGHT: 30, // Minimum height for hanging in inches
};

// ========================================================
// COLUMN DUPLICATION
// ========================================================

/**
 * Check if there's enough space to duplicate a column
 * 
 * @param {Array} items - All project items
 * @param {string} wallId - Wall ID
 * @param {string} itemId - Any item ID in the column to duplicate
 * @param {number} wallLength - Total wall length
 * @param {Object} usableInfo - { startOffset, endOffset, usableWidth }
 * @returns {{ canDuplicate: boolean, error: string|null, newX: number }}
 */
export const canDuplicateColumn = (items, wallId, itemId, wallLength, usableInfo) => {
  const column = getColumnForItem(items, wallId, itemId);
  if (!column) {
    return { canDuplicate: false, error: 'Column not found', newX: 0 };
  }
  
  const { startOffset = 0, endOffset = 0, usableWidth } = usableInfo || {};
  const columns = deriveColumnsFromItems(items, wallId);
  
  // Find the column index
  const columnIndex = columns.findIndex(col => 
    Math.abs(col.x - column.x) < 0.01 && col.width === column.width
  );
  
  // New column would be placed right after the original
  const newColumnX = column.x + column.width;
  const newColumnEnd = newColumnX + column.width;
  
  // Calculate total width after duplication
  const currentTotalWidth = columns.reduce((sum, col) => sum + col.width, 0);
  const newTotalWidth = currentTotalWidth + column.width;
  
  // Check usable width
  const maxUsable = usableWidth !== undefined ? usableWidth : (wallLength - startOffset - endOffset);
  
  if (newTotalWidth > maxUsable) {
    return {
      canDuplicate: false,
      error: `Not enough space to duplicate this column. Need ${column.width}" but only ${(maxUsable - currentTotalWidth).toFixed(1)}" available.`,
      newX: 0
    };
  }
  
  // Check if would extend into end buffer
  if (endOffset > 0 && (startOffset + newTotalWidth) > (wallLength - endOffset)) {
    return {
      canDuplicate: false,
      error: 'Duplicated column would extend into corner buffer zone.',
      newX: 0
    };
  }
  
  return { canDuplicate: true, error: null, newX: newColumnX };
};

/**
 * Create duplicate items for a column
 * Returns new items to be added and updates for existing items (horizontal reflow)
 * 
 * @param {Array} items - All project items
 * @param {string} wallId - Wall ID
 * @param {string} itemId - Any item ID in the column to duplicate
 * @param {Function} generateId - Function to generate new IDs (e.g., uuidv4)
 * @returns {{ newItems: Array, updates: Array }}
 */
export const createColumnDuplicates = (items, wallId, itemId, generateId) => {
  const column = getColumnForItem(items, wallId, itemId);
  if (!column) {
    return { newItems: [], updates: [] };
  }
  
  const columns = deriveColumnsFromItems(items, wallId);
  
  // Find the column index
  const columnIndex = columns.findIndex(col => 
    Math.abs(col.x - column.x) < 0.01 && col.width === column.width
  );
  
  // Position for new column
  const newColumnX = column.x + column.width;
  
  // Create duplicated items with new IDs and updated X
  const newItems = column.items.map(item => ({
    ...item,
    id: generateId(),
    x: newColumnX,
    // Preserve all other properties
  }));
  
  // Calculate updates for columns to the right (shift by column width)
  const updates = [];
  for (let i = columnIndex + 1; i < columns.length; i++) {
    const col = columns[i];
    col.items.forEach(item => {
      updates.push({ 
        id: item.id, 
        x: item.x + column.width 
      });
    });
  }
  
  return { newItems, updates };
};

// ========================================================
// WALL LAYOUT COPY
// ========================================================

/**
 * Check if a wall layout can be copied to another wall
 * 
 * @param {Array} items - All project items
 * @param {string} sourceWallId - Source wall ID
 * @param {string} targetWallId - Target wall ID
 * @param {Array} walls - All walls
 * @param {Object} buffersByWall - Buffer configuration
 * @returns {{ canCopy: boolean, error: string|null }}
 */
export const canCopyWallLayout = (items, sourceWallId, targetWallId, walls, buffersByWall) => {
  const sourceWall = walls.find(w => w.id === sourceWallId);
  const targetWall = walls.find(w => w.id === targetWallId);
  
  if (!sourceWall || !targetWall) {
    return { canCopy: false, error: 'Wall not found' };
  }
  
  // Get items on source wall
  const sourceItems = items.filter(i => i.wallId === sourceWallId);
  if (sourceItems.length === 0) {
    return { canCopy: false, error: 'Source wall has no components to copy' };
  }
  
  // Calculate source layout dimensions
  const sourceColumns = deriveColumnsFromItems(items, sourceWallId);
  const sourceUsedWidth = sourceColumns.reduce((sum, col) => sum + col.width, 0);
  const sourceMaxHeight = Math.max(...sourceItems.map(i => i.y + i.height));
  
  // Get source buffer info
  const sourceBuffers = buffersByWall?.[sourceWallId] || {};
  const sourceStartOffset = sourceBuffers.start?.sizeIn || 0;
  
  // Get target buffer info
  const targetBuffers = buffersByWall?.[targetWallId] || {};
  const targetStartOffset = targetBuffers.start?.sizeIn || 0;
  const targetEndOffset = targetBuffers.end?.sizeIn || 0;
  const targetUsableWidth = targetWall.length - targetStartOffset - targetEndOffset;
  
  // Check width
  if (sourceUsedWidth > targetUsableWidth) {
    return {
      canCopy: false,
      error: `Target wall is too narrow. Source layout uses ${sourceUsedWidth.toFixed(1)}" but target only has ${targetUsableWidth.toFixed(1)}" usable width.`
    };
  }
  
  // Check height
  const targetHeight = targetWall.height || sourceWall.height || 96;
  if (sourceMaxHeight > targetHeight) {
    return {
      canCopy: false,
      error: `Target wall is too short. Source layout needs ${sourceMaxHeight.toFixed(1)}" height but target is only ${targetHeight}".`
    };
  }
  
  return { canCopy: true, error: null };
};

/**
 * Create copied items for a wall layout
 * 
 * @param {Array} items - All project items
 * @param {string} sourceWallId - Source wall ID
 * @param {string} targetWallId - Target wall ID
 * @param {Object} buffersByWall - Buffer configuration
 * @param {Function} generateId - Function to generate new IDs
 * @returns {Array} New items for target wall
 */
export const createWallLayoutCopy = (items, sourceWallId, targetWallId, buffersByWall, generateId) => {
  // Get source items
  const sourceItems = items.filter(i => i.wallId === sourceWallId);
  if (sourceItems.length === 0) return [];
  
  // Get buffer offsets
  const sourceBuffers = buffersByWall?.[sourceWallId] || {};
  const targetBuffers = buffersByWall?.[targetWallId] || {};
  const sourceStartOffset = sourceBuffers.start?.sizeIn || 0;
  const targetStartOffset = targetBuffers.start?.sizeIn || 0;
  
  // Calculate X delta (adjust for different buffer positions)
  const xDelta = targetStartOffset - sourceStartOffset;
  
  // Create new items with adjusted positions
  const newItems = sourceItems.map(item => ({
    ...item,
    id: generateId(),
    wallId: targetWallId,
    x: item.x + xDelta,
    // Preserve all other properties
  }));
  
  return newItems;
};

